import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, Sparkles, Receipt } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';

const CartDrawer = () => {
  const { items, isOpen, closeCart, updateQuantity, removeItem, totalPrice } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const BOOKING_FEE = 1.0;

  const handleCheckout = () => {
    closeCart();
    if (user) {
      navigate('/checkout');
    } else {
      navigate('/login?redirect=/checkout');
    }
  };

  const totalItems = items.reduce((acc, it) => acc + it.quantity, 0);

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent
        side="right"
        className="!w-full sm:!max-w-md p-0 !bg-white border-l border-gray-100 flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header - dark navy with brand accent */}
        <div className="bg-gradient-to-br from-[#2D3E50] to-[#3D5266] text-white px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="font-['Playfair_Display'] text-2xl font-medium text-white flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#E8945A]/20 backdrop-blur-sm border border-[#E8945A]/30">
                <ShoppingBag className="h-4 w-4 text-[#E8945A]" />
              </span>
              Votre panier
            </SheetTitle>
            <SheetDescription className="text-white/60 text-xs uppercase tracking-[0.18em] pl-12">
              {totalItems > 0
                ? `${totalItems} article${totalItems > 1 ? 's' : ''} sélectionné${totalItems > 1 ? 's' : ''}`
                : 'Aucun article pour l\'instant'}
            </SheetDescription>
          </SheetHeader>
        </div>

        {items.length === 0 ? (
          /* Empty state */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-20 h-20 bg-[#FFF5ED] rounded-full flex items-center justify-center mb-5">
              <ShoppingBag className="h-9 w-9 text-[#E8945A]" />
            </div>
            <h3 className="font-['Playfair_Display'] text-xl text-[#2D3E50] mb-2">
              Votre panier est vide
            </h3>
            <p className="text-sm text-[#64748B] mb-6 max-w-xs">
              Découvrez nos kits de linge et services pour rendre votre séjour
              parfaitement confortable.
            </p>
            <Button
              onClick={() => { closeCart(); navigate('/products'); }}
              className="bg-[#2D3E50] hover:bg-[#3D4F61] rounded-lg h-11 px-6 gap-2"
              data-testid="browse-products-btn"
            >
              Découvrir nos produits
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3 bg-[#FAFAFA]">
              {items.map(({ product, quantity }) => (
                <div
                  key={product.id}
                  className="bg-white rounded-xl border border-gray-100 p-4 hover:border-[#E8945A]/30 hover:shadow-sm transition-all"
                  data-testid={`cart-item-${product.id}`}
                >
                  {/* Top row: name + remove */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-['Playfair_Display'] font-medium text-[15px] text-[#2D3E50] leading-tight">
                        {product.name}
                      </h4>
                      <p className="text-xs text-[#64748B] mt-0.5">
                        {product.price.toFixed(2)} € / unité
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(product.id)}
                      className="text-[#94A3B8] hover:text-red-500 transition-colors p-1 -m-1"
                      aria-label="Retirer"
                      data-testid={`remove-item-${product.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Bottom row: qty controls + line total */}
                  <div className="flex items-center justify-between">
                    <div className="inline-flex items-center bg-[#FAFAFA] rounded-lg border border-gray-200">
                      <button
                        onClick={() => updateQuantity(product.id, quantity - 1)}
                        className="h-9 w-9 flex items-center justify-center text-[#475569] hover:text-[#2D3E50] hover:bg-gray-100 rounded-l-lg transition-colors"
                        data-testid={`decrease-qty-${product.id}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-sm font-semibold text-[#2D3E50] w-8 text-center">
                        {quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(product.id, quantity + 1)}
                        className="h-9 w-9 flex items-center justify-center text-[#475569] hover:text-[#2D3E50] hover:bg-gray-100 rounded-r-lg transition-colors"
                        data-testid={`increase-qty-${product.id}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="font-['Playfair_Display'] text-lg font-medium text-[#E8945A]">
                      {(product.price * quantity).toFixed(2)} €
                    </div>
                  </div>
                </div>
              ))}

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-2 mt-5">
                <div className="rounded-lg bg-white border border-gray-100 p-3 flex items-start gap-2">
                  <Sparkles className="h-4 w-4 text-[#E8945A] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#475569] leading-tight">
                    Lavé en atelier pro
                  </p>
                </div>
                <div className="rounded-lg bg-white border border-gray-100 p-3 flex items-start gap-2">
                  <Truck className="h-4 w-4 text-[#5493CC] mt-0.5 flex-shrink-0" />
                  <p className="text-[11px] text-[#475569] leading-tight">
                    Livré dans le logement
                  </p>
                </div>
              </div>
            </div>

            {/* Footer with totals + CTA - solid white, sticky */}
            <div className="bg-white border-t border-gray-100 px-6 py-5 space-y-3 shadow-[0_-4px_16px_rgba(0,0,0,0.04)]">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#64748B]">Sous-total</span>
                  <span className="text-[#2D3E50] font-medium">{totalPrice.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between">
                  <span className="inline-flex items-center gap-1.5 text-[#64748B]">
                    <Receipt className="h-3.5 w-3.5" />
                    Frais de dossier
                  </span>
                  <span className="text-[#2D3E50] font-medium">{BOOKING_FEE.toFixed(2)} €</span>
                </div>
                <p className="text-[11px] text-[#94A3B8] italic">
                  Frais de livraison éventuels (express si commande &lt; 3 jours avant arrivée, coupure 16h) calculés à l'étape suivante.
                </p>
              </div>

              <div className="flex items-end justify-between pt-3 border-t border-gray-100">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#64748B]">
                    Total estimé
                  </div>
                  <div
                    className="font-['Playfair_Display'] text-3xl font-medium text-[#2D3E50] leading-none mt-0.5"
                    data-testid="cart-total"
                  >
                    {(totalPrice + BOOKING_FEE).toFixed(2)} €
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCheckout}
                className="w-full h-12 bg-[#E8945A] hover:bg-[#D4844A] rounded-lg text-base gap-2 shadow-md shadow-[#E8945A]/20"
                data-testid="checkout-btn"
              >
                Commander
                <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-[10px] text-center text-[#94A3B8]">
                Paiement sécurisé · CB · Apple Pay · Visa · Mastercard
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
